// src/components/MarketingLandingPage.jsx
// Full-screen marketing landing page for non-authenticated visitors

import React, { useEffect, useRef, useState } from 'react';
import JorvexStackRow from './JorvexStackRow';

const mono = 'JetBrains Mono, monospace';
const display = 'Syne, sans-serif';

// ── Scroll-reveal wrapper ────────────────────────────────────────────────────
function Reveal({ children, delay = 0, style = {} }) {
  const ref = useRef(null);
  const [show, setShow] = useState(false);
  useEffect(() => {
    const obs = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setShow(true); },
      { threshold: 0.06 }
    );
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, []);
  return (
    <div
      ref={ref}
      style={{
        opacity: show ? 1 : 0,
        transform: show ? 'none' : 'translateY(20px)',
        transition: `opacity .6s ease ${delay}s, transform .6s ease ${delay}s`,
        ...style,
      }}
    >
      {children}
    </div>
  );
}

// ── Kicker label ─────────────────────────────────────────────────────────────
function Kicker({ children, color = 'var(--cyan)' }) {
  return (
    <div style={{
      display: 'inline-block',
      padding: '4px 14px',
      background: `color-mix(in srgb, ${color} 12%, transparent)`,
      border: `1px solid color-mix(in srgb, ${color} 30%, transparent)`,
      borderRadius: 999,
      fontSize: 9,
      letterSpacing: '.18em',
      color,
      fontFamily: mono,
      fontWeight: 800,
      marginBottom: 18,
    }}>
      {children}
    </div>
  );
}

// ── Section heading ───────────────────────────────────────────────────────────
function SectionHeading({ kicker, title, sub, color = 'var(--cyan)', center = true }) {
  return (
    <div style={{ textAlign: center ? 'center' : 'left', marginBottom: 48 }}>
      <Reveal>
        {kicker && <Kicker color={color}>{kicker}</Kicker>}
        <h2 style={{ fontFamily: display, fontWeight: 900, fontSize: 'clamp(26px,3.5vw,42px)', lineHeight: 1.1, margin: '0 0 16px', color: 'var(--t1)' }}>
          {title}
        </h2>
        {sub && (
          <p style={{ fontSize: 14, color: 'var(--t2)', maxWidth: 600, margin: center ? '0 auto' : 0, lineHeight: 1.8 }}>
            {sub}
          </p>
        )}
      </Reveal>
    </div>
  );
}

// ── Data ──────────────────────────────────────────────────────────────────────
const HUB_WORKFLOWS = [
  { id: 'auto_eda',          icon: '📊', label: 'Auto EDA',             color: 'var(--cyan)',   desc: 'Upload any CSV. Get instant profiling, cleaning, distributions, correlations, and export-ready reports.' },
  { id: 'doc_analysis',      icon: '📄', label: 'Document Analysis',    color: 'var(--violet)', desc: 'Upload PDFs or text. Extract insights, summaries, entities, sentiment, keywords, and readability scores.' },
  { id: 'ml_experiments',    icon: '⚡', label: 'ML Experiments',       color: 'var(--orange)', desc: 'Upload data, engineer features, train classifiers and regressors, compare results, export reports.' },
  { id: 'image_insights',    icon: '🖼️', label: 'Image Insights',       color: 'var(--pink)',   desc: 'Upload images. Run color space analysis, edge detection, feature extraction, and filter comparisons.' },
  { id: 'speech_studio',     icon: '🎙️', label: 'Speech Studio',        color: 'var(--gold)',   desc: 'Upload audio or video. Transcribe, detect speakers, view waveforms, spectrograms, and MFCC analysis.' },
  { id: 'video_intelligence', icon: '🎞️', label: 'Video Intelligence', color: 'var(--orange)', desc: 'Upload video. Detect scenes, analyze motion, extract dominant colors, and review frame-by-frame.' },
  { id: 'local_agents',      icon: '🤖', label: 'Local AI Agents',      color: 'var(--green)',  desc: 'Build AI workers that run against your data locally. No API keys exposed. No cloud dependency.' },
];

const FEATURES = [
  { icon: '🔒', label: 'Local-First Privacy',      color: 'var(--cyan)',   desc: 'Your data runs in-browser. Nothing is sent to external servers by default. Sensitive data stays on your machine.' },
  { icon: '⚡', label: 'Zero Setup',               color: 'var(--green)',  desc: 'Open a browser tab and start. No environment setup, no dependency hell, no cloud account required to begin.' },
  { icon: '🧠', label: '50+ Research Labs',        color: 'var(--violet)', desc: 'From ML and deep learning to NLP, computer vision, generative AI, agentic systems, and quantum AI.' },
  { icon: '🤖', label: 'Built-In AI Agents',       color: 'var(--gold)',   desc: 'Create agents with memory and tool access that work alongside you on datasets, experiments, and ongoing research.' },
  { icon: '📋', label: 'Structured Workflows',     color: 'var(--orange)', desc: 'Not a blank canvas. Every module has structured tabs guiding you from upload to insight to report in minutes.' },
  { icon: '🏗️', label: 'Reproducible Pipelines',   color: 'var(--red)',    desc: 'Save experiment sessions, replay DAG action chains, and checkpoint progress across all modules.' },
];

const COMPARISON = [
  {
    tool: 'Jupyter / Colab',
    their: ['Great for notebooks', 'Any code you write', 'Manual tracking & setup'],
    ours: 'Ready-to-run workflows, no setup, AI agents, reproducible pipelines included.   ',
    color: 'var(--cyan)',
  },
  {
    tool: 'W&B / MLflow',
    their: ['Powerful experiment tracking', 'Setup + infrastructure', 'No learning layer'],
    ours: 'Zero-setup labs + built-in AI agents + track with no integration code needed.',
    color: 'var(--violet)',
  },
  {
    tool: 'ChatGPT / Claude',
    their: ['Helpful for queries', 'No project memory', 'No experiment context'],
    ours: 'Agents connected to your datasets, model runs, and real research history.',
    color: 'var(--green)',
  },
  {
    tool: 'Kaggle Notebooks',
    their: ['Great dataset hub', 'Notebook-first workflow', 'No longitudinal memory'],
    ours: 'Private local experiments with versioned outputs and an AI research partner.',
    color: 'var(--gold)',
  },
];

const FREE_CAPS = [
  { module: 'Auto EDA',           cap: '10 / month' },
  { module: 'Document Analysis',  cap: '10 / month' },
  { module: 'Image Insights',     cap: '100 / month' },
  { module: 'Speech Studio',      cap: '10 / month' },
  { module: 'ML Experiments',     cap: '10 / month' },
  { module: 'Video Intelligence', cap: '10 / month' },
];

const PLANS = [
  {
    tier: 'Free',
    price: '$0',
    sub: 'forever',
    hint: 'No card required',
    color: 'var(--t3)',
    border: 'var(--b1)',
    bullets: [
      'Access all 50+ labs and simulations',
      'Monthly free usage caps per module',
      'Account-linked progress tracking',
      'Community tier experience',
    ],
    cta: 'Create Free Account',
    ctaVariant: 'ghost',
  },
  {
    tier: 'Researcher',
    price: '$69',
    sub: '/quarter · or $199/year',
    hint: 'Best value on yearly',
    color: 'var(--cyan)',
    border: 'var(--cyan)',
    badge: 'MOST POPULAR',
    bullets: [
      'Unlimited Auto EDA, Document, ML, Image, Speech, Video runs',
      'Priority report generation and full exports',
      'Built-in AI agents, saved memory, and automation workflows',
      'Paid tier access across quarterly and yearly billing',
    ],
    cta: 'Start Researcher Plan',
    ctaVariant: 'primary',
  },
  {
    tier: 'Lifetime',
    price: '$420',
    sub: 'one-time',
    hint: 'All future upgrades included',
    color: 'var(--gold)',
    border: 'var(--gold)',
    bullets: [
      'Everything in Researcher',
      'One-time payment — no renewals ever',
      'Lifetime supporter badge and status',
      'All future premium upgrades included',
    ],
    cta: 'Get Lifetime Access',
    ctaVariant: 'gold',
  },
];

const STATS = [
  { label: 'Interactive Labs',    value: '50+',  color: 'var(--cyan)' },
  { label: 'Research Workflows',  value: '7',    color: 'var(--violet)' },
  { label: 'Browser-Native APIs', value: '12+',  color: 'var(--green)' },
  { label: 'Data Private',        value: '100%', color: 'var(--gold)' },
];

const PERSONAS = [
  {
    icon: '🎓',
    role: 'AI/ML Learners',
    color: 'var(--cyan)',
    desc: 'Work through interactive labs and simulations across machine learning, deep learning, NLP, computer vision, and more — with no setup.',
  },
  {
    icon: '🛠️',
    role: 'Builders & Engineers',
    color: 'var(--green)',
    desc: 'Upload data, run real pipelines, compare models, and produce reproducible experiment reports — all in the browser.',
  },
  {
    icon: '🔬',
    role: 'Researchers',
    color: 'var(--violet)',
    desc: 'Coordinate multi-source research with AI agents, automate analysis workflows, and build a traceable research trail.',
  },
];

const FAQ = [
  {
    q: 'Does my data get sent to a server?',
    a: 'No. All analysis and processing runs entirely in your browser using local JavaScript APIs (Web Audio API, Canvas API, etc). Your files are never uploaded unless you explicitly export a report.',
  },
  {
    q: 'What is the free tier?',
    a: 'The free tier gives you full access to all 50+ labs and simulations indefinitely. The research workflow hubs (Auto EDA, Document Analysis, etc.) have monthly usage caps. Paid tiers remove those caps.',
  },
  {
    q: 'What is the Researcher plan?',
    a: 'Researcher gives you unlimited runs on all 7 hub workflows, priority report generation, built-in AI agents with saved memory, and automation workflows. Available quarterly or yearly.',
  },
  {
    q: 'Can I cancel or change my plan?',
    a: 'Yes. Quarterly plans can be cancelled at any time. Yearly plans can be managed through the billing portal. Lifetime is a one-time payment with no renewals.',
  },
  {
    q: 'What browser do I need?',
    a: 'Modern Chromium-based browsers (Chrome, Edge, Brave) and Firefox work best. Some WebGL and ONNX features may require Chrome or Edge for best performance.',
  },
];

// ── Terminal animation ────────────────────────────────────────────────────────
const TERMINAL_LINES = [
  { txt: '> loading Auto EDA workspace...', color: 'var(--t3)' },
  { txt: '✓ CSV parsed — 12,500 rows · 18 columns', color: 'var(--green)' },
  { txt: '✓ null rates computed · 2 columns flagged', color: 'var(--green)' },
  { txt: '> running correlation matrix...', color: 'var(--t3)' },
  { txt: '✓ Pearson r computed on 12 numeric pairs', color: 'var(--green)' },
  { txt: '> generating EDA report...', color: 'var(--cyan)' },
  { txt: '✓ report ready · 8 sections · export as PDF', color: 'var(--cyan)' },
];

function TerminalPreview() {
  const [visibleLines, setVisibleLines] = useState(0);

  useEffect(() => {
    let index = 0;
    const id = setInterval(() => {
      index += 1;
      setVisibleLines(index);
      if (index >= TERMINAL_LINES.length) clearInterval(id);
    }, 520);
    return () => clearInterval(id);
  }, []);

  return (
    <div style={{
      background: 'var(--deep)',
      border: '1px solid var(--b1)',
      borderRadius: 8,
      padding: '18px 20px',
      fontFamily: mono,
      fontSize: 12,
      lineHeight: 1.9,
      maxWidth: 520,
      margin: '0 auto',
      boxShadow: '0 24px 80px rgba(0,217,255,.08)',
    }}>
      <div style={{ display: 'flex', gap: 6, marginBottom: 14 }}>
        {['var(--red)', 'var(--gold)', 'var(--green)'].map((c, i) => (
          <div key={i} style={{ width: 10, height: 10, borderRadius: '50%', background: c, opacity: .6 }} />
        ))}
      </div>
      {TERMINAL_LINES.slice(0, visibleLines).map((line, i) => (
        <div key={i} style={{ color: line.color, opacity: i < visibleLines - 1 ? 1 : 0.9 }}>
          {line.txt}
        </div>
      ))}
      {visibleLines < TERMINAL_LINES.length && (
        <span style={{ color: 'var(--cyan)', animation: 'ddBlink 1s step-end infinite' }}>█</span>
      )}
    </div>
  );
}

// ── FAQ Accordion ─────────────────────────────────────────────────────────────
function FAQItem({ q, a }) {
  const [open, setOpen] = useState(false);
  return (
    <div style={{ borderBottom: '1px solid var(--b1)' }}>
      <button
        onClick={() => setOpen(!open)}
        style={{
          width: '100%', background: 'none', border: 'none', cursor: 'pointer',
          padding: '18px 0', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 16,
        }}
      >
        <span style={{ fontFamily: mono, fontSize: 13, color: 'var(--t1)', textAlign: 'left' }}>{q}</span>
        <span style={{ color: 'var(--cyan)', fontSize: 18, flexShrink: 0, transform: open ? 'rotate(45deg)' : 'none', transition: 'transform .2s' }}>+</span>
      </button>
      {open && (
        <div style={{ paddingBottom: 18, fontSize: 13, color: 'var(--t2)', lineHeight: 1.8 }}>{a}</div>
      )}
    </div>
  );
}

// ── Main Export ───────────────────────────────────────────────────────────────
export default function MarketingLandingPage({ onSignup, onLogin, onEnterApp, onViewPricing }) {
  const sectionRef = useRef(null);

  useEffect(() => {
    if (typeof document === 'undefined' || typeof window === 'undefined') return;

    const title = 'About Jorvex | AI Research Workspace for Analysis, Experiments, and Agents';
    const description = 'Jorvex is an AI research workspace for dataset analysis, ML experiments, reports, and agent-assisted workflows. Explore the platform, pricing, and research stack.';
    const canonicalUrl = `${window.location.origin}/about`;

    document.title = title;

    const upsertMeta = (attribute, key, content) => {
      let node = document.head.querySelector(`meta[${attribute}="${key}"]`);
      if (!node) {
        node = document.createElement('meta');
        node.setAttribute(attribute, key);
        document.head.appendChild(node);
      }
      node.setAttribute('content', content);
    };

    let canonical = document.head.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      document.head.appendChild(canonical);
    }

    canonical.setAttribute('href', canonicalUrl);
    upsertMeta('name', 'description', description);
    upsertMeta('name', 'keywords', 'jorvex, ai research workspace, ml experiments, auto eda, agent workflows, ai analysis platform');
    upsertMeta('property', 'og:title', title);
    upsertMeta('property', 'og:description', description);
    upsertMeta('property', 'og:url', canonicalUrl);
    upsertMeta('property', 'og:type', 'website');
    upsertMeta('name', 'twitter:title', title);
    upsertMeta('name', 'twitter:description', description);
  }, []);

  const scrollToSection = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <div style={{ position: 'fixed', inset: 0, overflowY: 'auto', overflowX: 'hidden', background: 'var(--void)', zIndex: 200 }}>

      {/* ── BLINK KEYFRAME ── */}
      <style>{`
        @keyframes ddBlink { 0%,100%{opacity:1} 50%{opacity:0} }
        @keyframes ddHeroGlow { 0%,100%{opacity:.3} 50%{opacity:.7} }
        @keyframes ddScanLP { from{top:-4px} to{top:100%} }
        .dd-lp-hover-card:hover { border-color: var(--b2) !important; transform: translateY(-2px); transition: all .2s; }
        .dd-lp-nav-btn:hover { opacity: .8; }
        .dd-lp-plan-btn:hover { opacity: .85; }
        @media (max-width: 980px) { .jorvex-stack-row { grid-template-columns: repeat(2, minmax(0, 1fr)) !important; } }
        @media (max-width: 720px) { .jorvex-stack-row { grid-template-columns: 1fr !important; } }
      `}</style>

      {/* ══ STICKY NAVBAR ══ */}
      <nav style={{
        position: 'sticky', top: 0, zIndex: 50,
        height: 58, display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '0 clamp(16px,4vw,48px)',
        background: 'rgba(2,5,8,.92)', backdropFilter: 'blur(12px)',
        borderBottom: '1px solid var(--b0)',
      }}>
        {/* Logo */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <div style={{
            width: 32, height: 32, border: '1.5px solid var(--cyan)', borderRadius: 4,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            position: 'relative', overflow: 'hidden',
          }}>
            <div style={{ position: 'absolute', width: '100%', height: 2, background: 'var(--cyan)', opacity: .4, animation: 'ddScanLP 2.5s linear infinite', top: 0 }} />
            <span style={{ fontFamily: display, fontWeight: 800, fontSize: 15, color: 'var(--cyan)' }}>Ð</span>
          </div>
          <div>
            <div style={{ fontFamily: display, fontWeight: 800, fontSize: 14, letterSpacing: '.04em' }}>
              JOR<span style={{ color: 'var(--cyan)' }}>VEX</span>
            </div>
            <div style={{ fontSize: 8, color: 'var(--t3)', letterSpacing: '.12em' }}>AI RESEARCH WORKSPACE</div>
          </div>
        </div>

        {/* Nav links */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <button
            onClick={() => scrollToSection('lp-pricing')}
            className="dd-lp-nav-btn"
            style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--t2)', fontFamily: mono, fontSize: 10, letterSpacing: '.1em', padding: '6px 10px' }}
          >
            PRICING
          </button>
          <button
            onClick={onEnterApp}
            className="dd-lp-nav-btn"
            style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--t2)', fontFamily: mono, fontSize: 10, letterSpacing: '.1em', padding: '6px 10px' }}
          >
            UNLOCK LABS
          </button>
          <div style={{ width: 1, height: 20, background: 'var(--b1)' }} />
          <button
            onClick={onLogin}
            className="dd-lp-nav-btn"
            style={{ background: 'none', border: '1px solid var(--b1)', cursor: 'pointer', color: 'var(--t1)', fontFamily: mono, fontSize: 10, letterSpacing: '.1em', padding: '7px 14px', borderRadius: 4 }}
          >
            SIGN IN
          </button>
          <button
            onClick={onSignup}
            style={{ background: 'var(--cyan)', border: 'none', cursor: 'pointer', color: '#020508', fontFamily: mono, fontSize: 10, letterSpacing: '.1em', fontWeight: 900, padding: '8px 16px', borderRadius: 4 }}
          >
            GET STARTED FREE
          </button>
        </div>
      </nav>

      {/* ══ HERO ══ */}
      <section style={{ padding: 'clamp(60px,8vw,120px) clamp(16px,4vw,48px) clamp(60px,8vw,100px)', textAlign: 'center', maxWidth: 960, margin: '0 auto', position: 'relative' }}>
        {/* Glow orb */}
        <div style={{
          position: 'absolute', top: '20%', left: '50%', transform: 'translate(-50%,-50%)',
          width: 600, height: 300, borderRadius: '50%',
          background: 'radial-gradient(ellipse at center, rgba(0,217,255,.08) 0%, transparent 70%)',
          pointerEvents: 'none', animation: 'ddHeroGlow 5s ease-in-out infinite',
        }} />

        <Kicker>JORVEX · AI RESEARCH WORKSPACE · LOCAL-FIRST</Kicker>

        <h1 style={{
          fontFamily: display, fontWeight: 900, fontSize: 'clamp(36px,6vw,76px)',
          lineHeight: 1.05, margin: '0 0 24px', color: 'var(--t1)',
        }}>
          Jorvex is the workspace<br />
          <span style={{ color: 'var(--cyan)' }}>for AI analysis and experiments</span>
        </h1>

        <p style={{ fontSize: 'clamp(14px,1.6vw,17px)', color: 'var(--t2)', maxWidth: 680, margin: '0 auto 40px', lineHeight: 1.8 }}>
          Jorvex combines dataset analysis, ML workflows, report generation, and agent-assisted research in one browser-native environment.
          Explore the stack here, then sign in to unlock labs, models, and full experiment workflows.
        </p>

        {/* CTA Buttons */}
        <div style={{ display: 'flex', gap: 12, justifyContent: 'center', flexWrap: 'wrap', marginBottom: 56 }}>
          <button
            onClick={onSignup}
            style={{
              padding: '14px 28px', background: 'var(--cyan)', border: 'none', borderRadius: 6,
              color: '#020508', fontFamily: mono, fontSize: 11, letterSpacing: '.12em', fontWeight: 900, cursor: 'pointer',
              boxShadow: '0 8px 32px rgba(0,217,255,.25)',
            }}
          >
            GET STARTED FREE
          </button>
          <button
            onClick={onEnterApp}
            style={{
              padding: '14px 28px', background: 'transparent', border: '1px solid var(--b2)', borderRadius: 6,
              color: 'var(--t1)', fontFamily: mono, fontSize: 11, letterSpacing: '.12em', fontWeight: 700, cursor: 'pointer',
            }}
          >
            SIGN UP TO EXPLORE →
          </button>
        </div>

        {/* Stats row */}
        <div style={{ display: 'flex', gap: 'clamp(24px,4vw,56px)', justifyContent: 'center', flexWrap: 'wrap' }}>
          {STATS.map((s) => (
            <div key={s.label} style={{ textAlign: 'center' }}>
              <div style={{ fontFamily: display, fontWeight: 900, fontSize: 28, color: s.color }}>{s.value}</div>
              <div style={{ fontSize: 9, color: 'var(--t3)', letterSpacing: '.14em', marginTop: 4 }}>{s.label.toUpperCase()}</div>
            </div>
          ))}
        </div>
      </section>

      {/* ── Terminal preview ── */}
      <section style={{ padding: '0 clamp(16px,4vw,48px) clamp(60px,8vw,100px)' }}>
        <Reveal><TerminalPreview /></Reveal>
      </section>

      <section style={{ padding: '0 clamp(16px,4vw,48px) clamp(60px,8vw,100px)' }}>
        <div style={{ maxWidth: 1100, margin: '0 auto' }}>
          <SectionHeading
            kicker="THE RESEARCH STACK"
            title="Kaggle for datasets. Hugging Face for models. Jorvex for analysis and experiments."
            sub="This is the workflow Jorvex is built for: source data, source models, then run structured analysis and experiments in one place."
            color="var(--gold)"
          />
          <Reveal>
            <JorvexStackRow
              mono={mono}
              surface="var(--surface)"
              voidBg="var(--void)"
              border="var(--b1)"
              text="var(--t1)"
              muted="var(--t2)"
              showActions
              primaryLabel="SIGN UP"
              onPrimaryAction={onSignup}
              secondaryLabel="LOGIN"
              onSecondaryAction={onLogin}
            />
          </Reveal>
        </div>
      </section>

      {/* ══ FEATURES ══ */}
      <section style={{ padding: 'clamp(60px,8vw,100px) clamp(16px,4vw,48px)', borderTop: '1px solid var(--b0)' }}>
        <div style={{ maxWidth: 1100, margin: '0 auto' }}>
          <SectionHeading
            kicker="PLATFORM DESIGN"
            title="Built Around the Way Researchers Actually Work"
            sub="Not another isolated demo. A structured workspace for real analysis, real experiments, and real research output."
            color="var(--cyan)"
          />
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(300px,1fr))', gap: 16 }}>
            {FEATURES.map((f, i) => (
              <Reveal key={f.label} delay={i * 0.08}>
                <div
                  className="dd-lp-hover-card"
                  style={{
                    background: 'var(--surface)', border: '1px solid var(--b0)', borderRadius: 8,
                    padding: '22px 20px', cursor: 'default', transition: 'all .2s',
                  }}
                >
                  <div style={{ fontSize: 22, marginBottom: 12 }}>{f.icon}</div>
                  <div style={{ fontFamily: display, fontWeight: 800, fontSize: 15, color: f.color, marginBottom: 8 }}>{f.label}</div>
                  <div style={{ fontSize: 12, color: 'var(--t2)', lineHeight: 1.75 }}>{f.desc}</div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ══ WORKFLOW HUBS ══ */}
      <section style={{ padding: 'clamp(60px,8vw,100px) clamp(16px,4vw,48px)', background: 'var(--deep)', borderTop: '1px solid var(--b0)', borderBottom: '1px solid var(--b0)' }}>
        <div style={{ maxWidth: 1100, margin: '0 auto' }}>
          <SectionHeading
            kicker="RESEARCH HUBS"
            title="7 End-to-End Research Workflows"
            sub="Each hub takes you from raw data to insight to export — structured, guided, and ready to run."
            color="var(--violet)"
          />
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(280px,1fr))', gap: 14 }}>
            {HUB_WORKFLOWS.map((hub, i) => (
              <Reveal key={hub.id} delay={i * 0.07}>
                <div
                  className="dd-lp-hover-card"
                  style={{
                    background: 'var(--surface)', border: '1px solid var(--b0)', borderRadius: 8,
                    padding: '20px', cursor: 'default', transition: 'all .2s',
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
                    <div style={{
                      width: 36, height: 36, borderRadius: 6,
                      background: `color-mix(in srgb, ${hub.color} 12%, transparent)`,
                      border: `1px solid color-mix(in srgb, ${hub.color} 25%, transparent)`,
                      display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18, flexShrink: 0,
                    }}>
                      {hub.icon}
                    </div>
                    <div style={{ fontFamily: display, fontWeight: 800, fontSize: 14, color: hub.color }}>{hub.label}</div>
                  </div>
                  <div style={{ fontSize: 11, color: 'var(--t2)', lineHeight: 1.75 }}>{hub.desc}</div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ══ WHO IT'S FOR ══ */}
      <section style={{ padding: 'clamp(60px,8vw,100px) clamp(16px,4vw,48px)' }}>
        <div style={{ maxWidth: 1000, margin: '0 auto' }}>
          <SectionHeading
            kicker="DESIGNED FOR"
            title="Who Jorvex Is Built For"
            sub="Whether you are learning the fundamentals or shipping real research workflows, Jorvex gives you one environment instead of five disconnected tools."
            color="var(--green)"
          />
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(280px,1fr))', gap: 16 }}>
            {PERSONAS.map((p, i) => (
              <Reveal key={p.role} delay={i * 0.1}>
                <div style={{
                  background: 'var(--surface)', border: `1px solid color-mix(in srgb, ${p.color} 20%, transparent)`,
                  borderRadius: 8, padding: '24px 22px',
                }}>
                  <div style={{ fontSize: 28, marginBottom: 14 }}>{p.icon}</div>
                  <div style={{ fontFamily: display, fontWeight: 900, fontSize: 16, color: p.color, marginBottom: 10 }}>{p.role}</div>
                  <div style={{ fontSize: 12, color: 'var(--t2)', lineHeight: 1.8 }}>{p.desc}</div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ══ COMPARISON ══ */}
      <section style={{ padding: 'clamp(60px,8vw,100px) clamp(16px,4vw,48px)', background: 'var(--deep)', borderTop: '1px solid var(--b0)', borderBottom: '1px solid var(--b0)' }}>
        <div style={{ maxWidth: 1000, margin: '0 auto' }}>
          <SectionHeading
            kicker="VS THE ALTERNATIVES"
            title="Why Not Just Use What You Already Have?"
            sub="Other tools are good at isolated steps. Jorvex connects datasets, models, experiments, reports, and agent memory inside one workspace."
            color="var(--orange)"
          />
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(300px,1fr))', gap: 16 }}>
            {COMPARISON.map((c, i) => (
              <Reveal key={c.tool} delay={i * 0.09}>
                <div style={{ background: 'var(--surface)', border: '1px solid var(--b0)', borderRadius: 8, overflow: 'hidden' }}>
                  <div style={{ padding: '14px 18px', borderBottom: '1px solid var(--b0)', background: `color-mix(in srgb, ${c.color} 6%, transparent)` }}>
                    <div style={{ fontSize: 9, color: 'var(--t3)', letterSpacing: '.12em', fontFamily: mono }}>VS</div>
                    <div style={{ fontFamily: display, fontWeight: 900, fontSize: 15, color: c.color }}>{c.tool}</div>
                  </div>
                  <div style={{ padding: '14px 18px' }}>
                    <div style={{ marginBottom: 10 }}>
                      <div style={{ fontSize: 9, color: 'var(--t3)', letterSpacing: '.12em', marginBottom: 6, fontFamily: mono }}>THEM</div>
                      {c.their.map((t) => (
                        <div key={t} style={{ display: 'flex', alignItems: 'flex-start', gap: 7, marginBottom: 4 }}>
                          <span style={{ color: 'var(--t3)', fontSize: 10, marginTop: 2, flexShrink: 0 }}>—</span>
                          <span style={{ fontSize: 11, color: 'var(--t3)' }}>{t}</span>
                        </div>
                      ))}
                    </div>
                    <div>
                      <div style={{ fontSize: 9, color: c.color, letterSpacing: '.12em', marginBottom: 6, fontFamily: mono }}>JORVEX</div>
                      <div style={{ fontSize: 11, color: 'var(--t1)', lineHeight: 1.7 }}>{c.ours}</div>
                    </div>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ══ PRICING ══ */}
      <section id="lp-pricing" style={{ padding: 'clamp(60px,8vw,100px) clamp(16px,4vw,48px)' }}>
        <div style={{ maxWidth: 1000, margin: '0 auto' }}>
          <SectionHeading
            kicker="RESEARCH PLANS"
            title="Start Free. Upgrade When You're Ready."
            sub="Free tier is genuinely useful. Paid tier is for researchers who need unlimited, uninterrupted workflows."
            color="var(--cyan)"
          />

          {/* Free tier caps info */}
          <Reveal>
            <div style={{ background: 'var(--surface)', border: '1px solid var(--b0)', borderRadius: 8, padding: '18px 22px', marginBottom: 32 }}>
              <div style={{ fontSize: 9, color: 'var(--t3)', letterSpacing: '.14em', fontFamily: mono, marginBottom: 12 }}>FREE TIER MONTHLY CAPS</div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
                {FREE_CAPS.map((cap) => (
                  <div key={cap.module} style={{
                    padding: '5px 12px', background: 'var(--deep)', border: '1px solid var(--b1)', borderRadius: 4,
                    fontSize: 10, fontFamily: mono, color: 'var(--t2)',
                  }}>
                    <span style={{ color: 'var(--t1)' }}>{cap.module}</span> — {cap.cap}
                  </div>
                ))}
              </div>
            </div>
          </Reveal>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(280px,1fr))', gap: 16 }}>
            {PLANS.map((plan, i) => (
              <Reveal key={plan.tier} delay={i * 0.1}>
                <div style={{
                  background: 'var(--surface)',
                  border: `${plan.tier === 'Researcher' ? '2px' : '1px'} solid ${plan.border}`,
                  borderRadius: 8, padding: '28px 24px',
                  boxShadow: plan.tier === 'Researcher' ? `0 20px 60px color-mix(in srgb, ${plan.color} 12%, transparent)` : 'none',
                  position: 'relative', display: 'flex', flexDirection: 'column', gap: 18,
                }}>
                  {plan.badge && (
                    <div style={{
                      position: 'absolute', top: -14, left: '50%', transform: 'translateX(-50%)',
                      background: plan.color, color: '#020508', fontFamily: display, fontWeight: 900,
                      fontSize: 9, letterSpacing: '.14em', padding: '4px 14px', borderRadius: 999, whiteSpace: 'nowrap',
                    }}>
                      {plan.badge}
                    </div>
                  )}
                  <div>
                    <div style={{ fontSize: 9, color: plan.color, letterSpacing: '.16em', fontFamily: mono, fontWeight: 800, marginBottom: 8 }}>{plan.tier.toUpperCase()}</div>
                    <div style={{ fontFamily: display, fontWeight: 900, fontSize: 38, color: 'var(--t1)', lineHeight: 1.1 }}>{plan.price}</div>
                    <div style={{ fontSize: 11, color: 'var(--t3)', marginTop: 4 }}>{plan.sub}</div>
                    <div style={{ fontSize: 10, color: plan.color, marginTop: 6, fontFamily: mono }}>{plan.hint}</div>
                  </div>
                  <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'grid', gap: 8 }}>
                    {plan.bullets.map((b) => (
                      <li key={b} style={{ display: 'flex', alignItems: 'flex-start', gap: 8, fontSize: 11, color: 'var(--t2)', lineHeight: 1.6 }}>
                        <span style={{ color: 'var(--green)', flexShrink: 0, marginTop: 1 }}>✓</span>
                        {b}
                      </li>
                    ))}
                  </ul>
                  <button
                    onClick={plan.ctaVariant === 'ghost' ? onSignup : () => { onSignup?.(); }}
                    className="dd-lp-plan-btn"
                    style={{
                      marginTop: 'auto',
                      padding: '12px',
                      border: plan.ctaVariant === 'primary' ? 'none' : plan.ctaVariant === 'gold' ? `1px solid ${plan.color}44` : '1px solid var(--b1)',
                      background: plan.ctaVariant === 'primary' ? 'var(--cyan)' : plan.ctaVariant === 'gold' ? `color-mix(in srgb, ${plan.color} 12%, transparent)` : 'var(--b0)',
                      color: plan.ctaVariant === 'primary' ? '#020508' : plan.ctaVariant === 'gold' ? plan.color : 'var(--t1)',
                      fontFamily: mono, fontSize: 10, letterSpacing: '.1em', fontWeight: 900, cursor: 'pointer', borderRadius: 6,
                    }}
                  >
                    {plan.cta.toUpperCase()}
                  </button>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ══ LABS PREVIEW ══ */}
      <section style={{ padding: 'clamp(60px,8vw,100px) clamp(16px,4vw,48px)', background: 'var(--deep)', borderTop: '1px solid var(--b0)', borderBottom: '1px solid var(--b0)' }}>
        <div style={{ maxWidth: 900, margin: '0 auto' }}>
          <SectionHeading
            kicker="50+ INTERACTIVE LABS"
            title="From ML Fundamentals to Quantum AI"
            sub="Every track includes hands-on, interactive labs running entirely in your browser."
            color="var(--violet)"
          />
          <Reveal>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, justifyContent: 'center' }}>
              {[
                { label: 'Machine Learning', color: 'var(--cyan)' },
                { label: 'Deep Learning', color: 'var(--violet)' },
                { label: 'Computer Vision', color: 'var(--blue)' },
                { label: 'Natural Language Processing', color: 'var(--green)' },
                { label: 'Generative AI', color: 'var(--pink)' },
                { label: 'Agentic AI', color: 'var(--gold)' },
                { label: 'Reinforcement Learning', color: 'var(--orange)' },
                { label: 'Quantum AI', color: 'var(--violet)' },
                { label: 'Edge AI', color: 'var(--teal)' },
                { label: 'Data Science', color: 'var(--cyan)' },
              ].map((track) => (
                <div
                  key={track.label}
                  style={{
                    padding: '7px 16px',
                    background: `color-mix(in srgb, ${track.color} 8%, transparent)`,
                    border: `1px solid color-mix(in srgb, ${track.color} 22%, transparent)`,
                    borderRadius: 999, fontSize: 11, color: track.color, fontFamily: mono,
                  }}
                >
                  {track.label}
                </div>
              ))}
            </div>
          </Reveal>
          <Reveal delay={0.1}>
            <div style={{ textAlign: 'center', marginTop: 32 }}>
              <button
                onClick={onEnterApp}
                style={{
                  padding: '12px 24px', background: 'transparent', border: '1px solid var(--violet)', borderRadius: 6,
                  color: 'var(--violet)', fontFamily: mono, fontSize: 10, letterSpacing: '.12em', fontWeight: 800, cursor: 'pointer',
                }}
              >
                EXPLORE ALL LABS →
              </button>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ══ FAQ ══ */}
      <section style={{ padding: 'clamp(60px,8vw,100px) clamp(16px,4vw,48px)' }}>
        <div style={{ maxWidth: 700, margin: '0 auto' }}>
          <SectionHeading
            kicker="FAQ"
            title="Common Questions"
            color="var(--gold)"
          />
          <Reveal>
            <div>
              {FAQ.map((item) => <FAQItem key={item.q} q={item.q} a={item.a} />)}
            </div>
          </Reveal>
        </div>
      </section>

      {/* ══ FINAL CTA ══ */}
      <section style={{ padding: 'clamp(60px,8vw,100px) clamp(16px,4vw,48px)', background: 'var(--deep)', borderTop: '1px solid var(--b0)' }}>
        <div style={{ maxWidth: 700, margin: '0 auto', textAlign: 'center' }}>
          <Reveal>
            <Kicker>START NOW · NO CARD REQUIRED</Kicker>
            <h2 style={{ fontFamily: display, fontWeight: 900, fontSize: 'clamp(28px,4vw,52px)', lineHeight: 1.1, margin: '0 0 20px', color: 'var(--t1)' }}>
              Ready to stop juggling tools<br />
              <span style={{ color: 'var(--cyan)' }}>and start researching?</span>
            </h2>
            <p style={{ fontSize: 14, color: 'var(--t2)', maxWidth: 500, margin: '0 auto 36px', lineHeight: 1.8 }}>
              Your free account takes 30 seconds to create. No credit card. Start inside Jorvex with the guided labs, then upgrade only when you need uninterrupted workflows.
            </p>
            <div style={{ display: 'flex', gap: 12, justifyContent: 'center', flexWrap: 'wrap' }}>
              <button
                onClick={onSignup}
                style={{
                  padding: '14px 32px', background: 'var(--cyan)', border: 'none', borderRadius: 6,
                  color: '#020508', fontFamily: mono, fontSize: 11, letterSpacing: '.12em', fontWeight: 900, cursor: 'pointer',
                  boxShadow: '0 8px 32px rgba(0,217,255,.2)',
                }}
              >
                CREATE FREE ACCOUNT
              </button>
              <button
                onClick={onLogin}
                style={{
                  padding: '14px 24px', background: 'transparent', border: '1px solid var(--b2)', borderRadius: 6,
                  color: 'var(--t1)', fontFamily: mono, fontSize: 11, letterSpacing: '.12em', fontWeight: 700, cursor: 'pointer',
                }}
              >
                SIGN IN
              </button>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ══ FOOTER ══ */}
      <footer style={{ padding: '28px clamp(16px,4vw,48px)', borderTop: '1px solid var(--b0)', display: 'flex', gap: 16, flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'center' }}>
        <div style={{ fontFamily: mono, fontSize: 10, color: 'var(--t3)' }}>
          © {new Date().getFullYear()} Jorvex
        </div>
        <div style={{ display: 'flex', gap: 16 }}>
          {[
            { label: 'Privacy', href: '/privacy/' },
            { label: 'Terms', href: '/terms/' },
            { label: 'Pricing', onClick: onViewPricing },
          ].map((link) => (
            link.href ? (
              <a
                key={link.label}
                href={link.href}
                style={{ fontSize: 10, color: 'var(--t3)', textDecoration: 'none', fontFamily: mono, letterSpacing: '.08em' }}
              >
                {link.label}
              </a>
            ) : (
              <button
                key={link.label}
                onClick={link.onClick}
                style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 10, color: 'var(--t3)', fontFamily: mono, letterSpacing: '.08em' }}
              >
                {link.label}
              </button>
            )
          ))}
        </div>
      </footer>

    </div>
  );
}
